// ---------------------------------------------------------------------------
// Approval: Copilot paused on a change and waits for the user's yes or no.
//
// The stream ends the paused answer with `{"event": {"type": "usage", "stop": "interrupt", "interrupts": [{id, prompt}]}}`.
// `prompt` reads `<what it does>: <JSON of the arguments>`. The answer goes back as `interrupt_response: {id, approve}`;
// one answer covers every action paused in the same step (the service applies it to all).
// ---------------------------------------------------------------------------

// Ids and plumbing fields stay off the card unless nothing else is left to show (a bare "Delete a dashboard" must say which one).
const INTERNAL_KEY = /(^|_)ids?$|^message_(column|table)$/;

function readableDetails(details) {
  const readable = details.filter(([name]) => !INTERNAL_KEY.test(name));
  return readable.length ? readable : details;
}

// Raw JSON arguments go under "Advanced"; a dashboard's widgets get a readable list instead.
const ADVANCED_KEY = /_json$/;
const WIDGET_TITLES = /"title"\s*:\s*"((?:[^"\\]|\\.)*)"(?:\s*,\s*"chart_type"\s*:\s*"(\w+)")?/g;

/** One line per widget, `• Title (chart type)`; reads the JSON, or the titles out of text that was cut for length. */
export function widgetsSummary(raw) {
  let items = null;
  try {
    const list = JSON.parse(raw);
    if (Array.isArray(list)) items = list.map((w) => [w?.title, w?.chart_type]);
  } catch {
    // cut text: the pattern below reads it
  }
  if (!items) items = [...String(raw).matchAll(WIDGET_TITLES)].map((m) => [m[1], m[2]]);
  return items
    .filter(([title]) => title)
    .map(([title, type]) => `\u2022 ${title}${type ? ` (${type})` : ''}`)
    .join('\n');
}

/** One paused action as `{id, title, details, advanced}` (both `[[name, value], ...]`); arguments only when the prompt carries valid JSON. */
export function parsePrompt({ id, prompt }) {
  const text = String(prompt ?? '');
  const at = text.indexOf(': {');
  if (at < 0) return { id, title: text, details: [], advanced: [] };
  try {
    const args = JSON.parse(text.slice(at + 2));
    const details = [];
    const advanced = [];
    Object.entries(args).forEach(([name, raw]) => {
      const value = typeof raw === 'string' ? raw : JSON.stringify(raw);
      if (!ADVANCED_KEY.test(name)) {
        details.push([name, value]);
        return;
      }
      advanced.push([name, value]);
      const summary = name === 'widgets_json' ? widgetsSummary(value) : '';
      if (summary) details.push(['widgets', summary]);
    });
    return { id, title: text.slice(0, at), details: readableDetails(details), advanced };
  } catch {
    return { id, title: text, details: [], advanced: [] };
  }
}

const COLOR_LIST = /^#[0-9a-f]{6}(\s*,\s*#[0-9a-f]{6})*$/i;

/** The colours of a value like `#F97316, #DC2626`, or null for anything else (so it is safe to paint). */
export function colorsOf(value) {
  return COLOR_LIST.test(String(value).trim()) ? String(value).split(',').map((color) => color.trim()) : null;
}

/** The approval to show for a stream event, or null: `{id, actions}` where `id` is the one to answer. */
export function approvalOf(event) {
  if (!event || event.type !== 'usage' || !Array.isArray(event.interrupts) || !event.interrupts.length) return null;
  const actions = event.interrupts.filter((i) => i && i.id).map(parsePrompt);
  return actions.length ? { id: actions[0].id, actions } : null;
}

/** The body field that answers an approval. */
export function interruptResponseOf(id, approve) {
  return { id, approve: approve === true };
}
